import { useFormField } from '@calendly/form'
import { I18n } from '@calendly/i18n'
import { useClose } from '@calendly/ui/components/dropdown'
import { nanoid } from 'nanoid'
import { useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { attributeValuesSelector } from '../selectors'
import { addAttributeValue, removeAttributeValue, updateAttributeValue } from '../slice'
import { OptionsList } from './searchable-select-field'
import { StatusBadge, StyledCreateGroupOption, StyledSearchableSelectField } from './styles'


const CreateAttributeValueOption = ({ children, onClick }) => {
  const closeDropdown = useClose()
  const onClickHandler = () => {
    onClick?.(closeDropdown)
  }
  return (
    <StyledCreateGroupOption
      role="option"
      onClick={onClickHandler}
    >
      {children}
    </StyledCreateGroupOption>
  )
}

const SelectedValueNode = ({ attributeValue }) => (
  <div>
    {attributeValue.name}
    {attributeValue.isNew ? (
      <>
        {' '}
        <StatusBadge>{I18n.t('publisher.organization_settings.user_provisioning.groups.calendly_group.new_label')}</StatusBadge>
      </>
    ) : null}
  </div>
)

function findMatchingAttributeFn(newValue) {
  return (attrValue) => attrValue.name.toLowerCase() === newValue.toLowerCase().trim()
}

/**
 * @typedef {Object} AttributeValue
 * @property {string} id - Used to prevent the `Each child in a list should have a unique “key” prop` react error
 * @property {string} name - The name
 * @property {boolean} isNew - To add the badge once you select one attribute value
 * @property {boolean} isUsed - If it's used already in one group-mapping, helps out to filter the value in another select.
 */

export const AttributeValueSelect = (props) => {
  const dispatch = useDispatch()
  const attributeValues = useSelector(attributeValuesSelector)
  const [{ value }, _meta, { setValue, setTouched }] = useFormField({ name: props.name })
  const [query, setQuery] = useState('')

  // Retrieves the corresponding object from the attributeValues list in the state, based on the form field's selected value.
  /** @type {AttributeValue|undefined} */
  const selectedAttributeValue = attributeValues.find(findMatchingAttributeFn(value))
  // Retrieves the corresponding object from the attributeValues list in the state, based on the query value.
  /** @type {AttributeValue|undefined} */
  const attributeValueFromQuery = attributeValues.find(findMatchingAttributeFn(query || ''))

  const options = useMemo(() => (
    attributeValues
      .filter((attrValue) => {
        if (attrValue.id !== selectedAttributeValue?.id && attrValue.isUsed) {
          return false
        }

        if (query) {
          return attrValue.name.startsWith(query)
        }

        return true
      })
  ), [selectedAttributeValue?.id, attributeValues, query])

  const unSelectCurrentAttribute = useCallback(() => {
    if (!selectedAttributeValue) {
      return
    }
    if (selectedAttributeValue.isNew) {
      dispatch(removeAttributeValue(selectedAttributeValue.id))
      return
    }
    const currentAttributeValueIndex = attributeValues.findIndex(findMatchingAttributeFn(value))
    dispatch(updateAttributeValue({ index: currentAttributeValueIndex, values: { isUsed: false } }))
  }, [selectedAttributeValue, attributeValues, dispatch, value])


  const onCreate = useCallback((newValue) => {
    if (!newValue || attributeValueFromQuery) {
      return
    }
    unSelectCurrentAttribute()
    setQuery(newValue.trim())
    setValue(newValue.trim())
    setTouched(true)
    dispatch(addAttributeValue({
      id: nanoid(10),
      name: newValue,
      isNew: true,
      isUsed: true,
    }))
  }, [attributeValueFromQuery, dispatch, setTouched, setValue, unSelectCurrentAttribute])


  const handleOnSearch = (searchQuery) => {
    setQuery(searchQuery)
  }

  const handleOnChange = (changedValue) => {
    const attributeValueIndex = attributeValues.findIndex(findMatchingAttributeFn(changedValue))
    if (attributeValueIndex === -1) {
      return
    }

    unSelectCurrentAttribute()

    dispatch(updateAttributeValue({ index: attributeValueIndex, values: { isUsed: true } }))
    setQuery(changedValue)
    setValue(changedValue)
  }

  const handleOnCreateAttributeValue = (closeDropdown) => {
    closeDropdown()
    onCreate(query)
  }

  const handleOnUpdate = (event) => {
    if (event.type === 'keydown' && event.code === 'Enter' && query && !attributeValueFromQuery) {
      onCreate(query)
    }
  }

  return (
    <StyledSearchableSelectField
      displayValue={
        attributeValueFromQuery
          ? (
            <SelectedValueNode
              attributeValue={attributeValueFromQuery}
            />
          ) : value
      }
      placeholder={I18n.t('publisher.organization_settings.user_provisioning.groups.attribute_value.placeholder')}
      options={options}
      onSearch={handleOnSearch}
      defaultSearchQuery={value}
      onUpdate={handleOnUpdate}
      onChange={handleOnChange}
      {...props}
    >
      <OptionsList
        notFoundText={I18n.t('publisher.event_types.scope_dropdown.empty_search_state.title')}
        onChangeProperty="name"
      >
        {
          query && !attributeValueFromQuery
            ? (
              <CreateAttributeValueOption
                role="option"
                key="createAttributeValue"
                onClick={handleOnCreateAttributeValue}
              >
                {I18n.t('publisher.organization_settings.user_provisioning.groups.create_group', { group_name: query })}
              </CreateAttributeValueOption>
            )
            : null
        }
      </OptionsList>
    </StyledSearchableSelectField>
  )
}
